/**
 * University Filters Component - Modern Design
 * Beautiful sidebar with modern filters
 */

import React, { useState, useEffect } from 'react';
import { Button } from '@voilajsx/uikit/button';
import { Label } from '@voilajsx/uikit/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@voilajsx/uikit/select';
import { RotateCcw, SlidersHorizontal } from 'lucide-react';
import type { FilterOptions, BrowseFilters } from '../types';

interface UniversityFiltersProps {
  filters: BrowseFilters;
  filterOptions: FilterOptions;
  onFilterChange: (filters: BrowseFilters) => void;
  onReset: () => void;
}

export const UniversityFilters: React.FC<UniversityFiltersProps> = ({
  filters,
  filterOptions,
  onFilterChange,
  onReset
}) => {
  const [localFilters, setLocalFilters] = useState<BrowseFilters>(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleApply = () => {
    onFilterChange(localFilters);
  };

  const hasActiveFilters =
    localFilters.field ||
    localFilters.country ||
    localFilters.budgetMin ||
    localFilters.budgetMax ||
    localFilters.rankingMax;

  return (
    <div className="bg-card rounded-3xl p-7 shadow-sm border border-border">
      {/* Header */}
      <div className="flex items-center justify-between mb-7">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <SlidersHorizontal className="h-5 w-5 text-primary" />
          </div>
          <h2 className="text-xl font-bold text-foreground m-0">
            Filters
          </h2>
        </div>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onReset}
            className="text-muted-foreground px-3 py-1.5 text-sm font-semibold"
          >
            <RotateCcw className="h-4 w-4 mr-1" />
            Reset
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-6">
        {/* Academic Field */}
        <div>
          <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2.5 block">
            Academic Field
          </Label>
          <Select
            value={localFilters.field || 'all'}
            onValueChange={(value) =>
              setLocalFilters({ ...localFilters, field: value === 'all' ? undefined : value })
            }
          >
            <SelectTrigger className="bg-muted border-border rounded-xl px-4 py-3 text-[15px] font-medium text-foreground">
              <SelectValue placeholder="All fields" />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border rounded-xl shadow-lg">
              <SelectItem value="all" className="text-[15px]">All Fields</SelectItem>
              {filterOptions.fields.map((field) => (
                <SelectItem key={field} value={field} className="text-[15px]">
                  {field}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Country */}
        <div>
          <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2.5 block">
            Country
          </Label>
          <Select
            value={localFilters.country || 'all'}
            onValueChange={(value) =>
              setLocalFilters({ ...localFilters, country: value === 'all' ? undefined : value })
            }
          >
            <SelectTrigger className="bg-muted border-border rounded-xl px-4 py-3 text-[15px] font-medium text-foreground">
              <SelectValue placeholder="All countries" />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border rounded-xl shadow-lg max-h-80">
              <SelectItem value="all" className="text-[15px]">All Countries</SelectItem>
              {filterOptions.countries.map((country) => (
                <SelectItem key={country.id} value={country.name} className="text-[15px]">
                  {country.flag_emoji} {country.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Budget Range */}
        <div>
          <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2.5 block">
            Annual Tuition Budget
          </Label>
          <Select
            value={localFilters.budgetMax?.toString() || 'all'}
            onValueChange={(value) => {
              if (value === 'all') {
                setLocalFilters({
                  ...localFilters,
                  budgetMin: undefined,
                  budgetMax: undefined
                });
              } else {
                const range = filterOptions.budgetRanges.find(r => r.max.toString() === value);
                if (range) {
                  setLocalFilters({
                    ...localFilters,
                    budgetMin: range.min,
                    budgetMax: range.max
                  });
                }
              }
            }}
          >
            <SelectTrigger className="bg-muted border-border rounded-xl px-4 py-3 text-[15px] font-medium text-foreground">
              <SelectValue placeholder="Any budget" />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border rounded-xl shadow-lg">
              <SelectItem value="all" className="text-[15px]">Any Budget</SelectItem>
              {filterOptions.budgetRanges.map((range) => (
                <SelectItem key={range.label} value={range.max.toString()} className="text-[15px]">
                  {range.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Ranking */}
        <div>
          <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2.5 block">
            QS World Ranking
          </Label>
          <Select
            value={localFilters.rankingMax?.toString() || 'all'}
            onValueChange={(value) =>
              setLocalFilters({
                ...localFilters,
                rankingMax: value === 'all' ? undefined : parseInt(value)
              })
            }
          >
            <SelectTrigger className="bg-muted border-border rounded-xl px-4 py-3 text-[15px] font-medium text-foreground">
              <SelectValue placeholder="Any ranking" />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border rounded-xl shadow-lg">
              <SelectItem value="all" className="text-[15px]">Any Ranking</SelectItem>
              {filterOptions.rankingRanges.map((range) => (
                <SelectItem key={range.label} value={range.max.toString()} className="text-[15px]">
                  {range.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Sort By */}
        <div>
          <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2.5 block">
            Sort By
          </Label>
          <Select
            value={localFilters.sort || 'ranking'}
            onValueChange={(value) =>
              setLocalFilters({ ...localFilters, sort: value })
            }
          >
            <SelectTrigger className="bg-muted border-border rounded-xl px-4 py-3 text-[15px] font-medium text-foreground">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border rounded-xl shadow-lg">
              <SelectItem value="ranking" className="text-[15px]">Best Ranking</SelectItem>
              <SelectItem value="tuition_asc" className="text-[15px]">Lowest Tuition</SelectItem>
              <SelectItem value="tuition_desc" className="text-[15px]">Highest Tuition</SelectItem>
              <SelectItem value="name" className="text-[15px]">Name (A-Z)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Apply Button */}
      <Button
        onClick={handleApply}
        className="w-full bg-primary text-primary-foreground hover:bg-primary/90 px-3.5 py-3.5 rounded-xl font-semibold text-[15px] mt-7"
      >
        Apply Filters
      </Button>
    </div>
  );
};
